import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme, withStyles } from '@material-ui/core'
import { DragDropContext } from 'react-beautiful-dnd'
import ArrowLeft from '@material-ui/icons/ArrowBackIosOutlined'
import ArrowRight from '@material-ui/icons/ArrowForwardIosOutlined'
import Badge from '@material-ui/core/Badge'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Paper from '@material-ui/core/Paper'
import Slide from '@material-ui/core/Slide'
import { Swipeable } from 'react-swipeable'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import TodoListItemEdit from '../todos/TodoListItemEdit'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { FOCUS } from '../../data/focus'
import LOG from '../../logging/Logger'
import DnDVirtualList from '../lists/DnDVirtualList'

const HIGHLIGHT_DISABLE = false

const CARD_DRAG_COLOUR = '#fff740'
const CARD_COLOUR = '#feff9c'

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    justifyContent: 'center'
  },
  tab: {
    fontSize: '0.7rem'
  },
  title: {
    paddingBottom: theme.spacing(2)
  },
  grid: {
    maxWidth: 2000,
    minHeight: '100vh',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4)
    }
  },
  paper: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0),
      paddingTop: theme.spacing(1)
    },
    color: theme.palette.text.secondary,
    minHeight: '100%'
  },
  card: {
    userSelect: 'none',
    minHeight: '100px',
    margin: theme.spacing(1)
  },
  cardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  itemText: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    overflowWrap: 'anywhere',
    display: 'inline-block',
    wordBreak: 'break-word',
    fontFamily: 'Annie Use Your Telescope'
  },
  arrow: {
    cursor: 'pointer'
  },
  badge: {
    paddingTop: 4
  }
}))

const kanbanColumns = {
  todo: {
    position: 0,
    name: 'Todo',
    todos: []
  },
  inprogress: {
    position: 1,
    name: 'In progress',
    todos: []
  },
  done: {
    position: 2,
    name: 'Done',
    todos: []
  }
}

const TabPanel = props => {
  LOG.debug('Render: PersonalKanban: TabPanel')

  const { children, value, index, ...other } = props

  return (
    <div
      style={{ height: '100vh' }}
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box style={{ height: '100vh', padding: 0 }} p={3}>
          {children}
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
}

const Item = ({
  classes,
  columnIndex,
  columnArray,
  todo,
  setEditItem,
  decrypt,
  editTodoStatus,
  getProjectNameForTodo,
  isDragging,
  getAreaById
}) => {
  LOG.debug('Render: PersonalKanban: Item')

  const theme = useTheme()

  const showArrows = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true
  })
  const largeFont = useMediaQuery(theme.breakpoints.up('lg'), {
    noSsr: true
  })

  const [sliding, setSliding] = useState(false)
  const [direction, setDirection] = useState('left')

  useEffect(() => {
    setSliding(false)
  }, [todo]) // Want to refresh the list whenever the today list or todos are updated

  const getHighlightColour = useCallback(
    defaultColour => {
      if (!HIGHLIGHT_DISABLE && todo.area) {
        const area = getAreaById(todo.area)
        if (area && area.colour) {
          return area.colour
        }
      }
      return defaultColour
    },
    [getAreaById, todo.area]
  )

  // Based on https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
  const getTextColour = useCallback(() => {
    if (!HIGHLIGHT_DISABLE && todo.area) {
      const area = getAreaById(todo.area)
      if (area && area.colour) {
        let hexColour = area.colour

        // If a leading # is provided, remove it
        if (hexColour.slice(0, 1) === '#') {
          hexColour = hexColour.slice(1)
        }

        // Convert to RGB value
        const r = parseInt(hexColour.substr(0, 2), 16)
        const g = parseInt(hexColour.substr(2, 2), 16)
        const b = parseInt(hexColour.substr(4, 2), 16)

        // Get YIQ ratio
        const yiq = (r * 299 + g * 587 + b * 114) / 1000

        // Check contrast
        return yiq >= 128 ? 'black' : 'white'
      }
    }
    return theme.palette.primary.contrastText
  }, [getAreaById, theme.palette.primary.contrastText, todo.area])

  const renderProjectName = useMemo(() => {
    if (todo.project) {
      const projectName = getProjectNameForTodo(todo)
      if (projectName) {
        return (
          <Typography className={classes.itemText} variant='subtitle2'>
            {'(' + projectName + ')'}
          </Typography>
        )
      }
    }
    return null
  }, [todo, classes.itemText, getProjectNameForTodo])

  return (
    <Slide
      in={!sliding}
      direction={direction}
      timeout={!sliding ? 0 : 500}
      onExited={() => {
        editTodoStatus(
          todo.id,
          columnArray[
            direction === 'left' ? columnIndex + 1 : columnIndex - 1
          ][0]
        )
      }}
    >
      <Card
        className={classes.card}
        onClick={e => setEditItem(todo.id)}
        style={{
          backgroundColor: getHighlightColour(
            isDragging ? CARD_DRAG_COLOUR : CARD_COLOUR
          ),
          color: getTextColour()
        }}
      >
        <CardContent className={classes.cardContent}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            minHeight={100}
          >
            <Grid container justifyContent='center'>
              <Grid item xs={2}>
                {showArrows && columnIndex > 0 && (
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    minHeight='100%'
                  >
                    <Tooltip
                      title={'Move to ' + columnArray[columnIndex - 1][1].name}
                    >
                      <ArrowLeft
                        className={classes.arrow}
                        onClick={e => {
                          setSliding(true)
                          setDirection('right')
                          e.stopPropagation()
                        }}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Grid>
              <Grid item xs={8}>
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  minHeight={100}
                >
                  <Typography
                    className={classes.itemText}
                    variant={largeFont ? 'h5' : 'h6'}
                  >
                    {decrypt(todo.name)}
                  </Typography>
                </Box>
                {renderProjectName}
              </Grid>
              <Grid item xs={2}>
                {showArrows && columnIndex < columnArray.length - 1 && (
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    minHeight='100%'
                  >
                    <Tooltip
                      title={'Move to ' + columnArray[columnIndex + 1][1].name}
                    >
                      <ArrowRight
                        className={classes.arrow}
                        onClick={e => {
                          setSliding(true)
                          setDirection('left')
                          e.stopPropagation()
                        }}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Slide>
  )
}

const Column = memo(
  ({
    classes,
    id,
    scrollRef,
    column,
    columnArray,
    columnIndex,
    wantTitle = true,
    decrypt,
    getRepeatPattern,
    getProjectsForAreas,
    clientId,
    createProject,
    deleteTodo,
    redirectToProject,
    getProjectById,
    editTodo,
    getAvailableTags,
    getAreas,
    editTodoStatus,
    getProjectNameForTodo,
    getTagsFromTodo,
    getAreaById,
    createAttachment,
    deleteAttachment,
    editAttachment,
    downloadAttachment,
    getTodoAttachments
  }) => {
    LOG.debug('Render: PersonalKanban: Column')

    const theme = useTheme()
    const [drawerOpen, setDrawerOpen] = useState(false)

    const renderListItem = useCallback(
      ({ index, dragHandleProps, isDragging }) => {
        return (
          <Fragment>
            <Item
              classes={classes}
              columnIndex={columnIndex}
              columnArray={columnArray}
              todo={column.todos[index]}
              setEditItem={setDrawerOpen}
              decrypt={decrypt}
              editTodoStatus={editTodoStatus}
              getProjectNameForTodo={getProjectNameForTodo}
              isDragging={isDragging}
              getAreaById={getAreaById}
            />
            <TodoListItemEdit
              todo={column.todos[index]}
              open={drawerOpen === column.todos[index].id}
              onClose={() => setDrawerOpen(false)}
              decrypt={decrypt}
              getRepeatPattern={getRepeatPattern}
              getProjectsForAreas={getProjectsForAreas}
              clientId={clientId}
              createProject={createProject}
              deleteTodo={deleteTodo}
              redirectToProject={redirectToProject}
              getProjectById={getProjectById}
              editTodo={editTodo}
              getAvailableTags={getAvailableTags}
              getTagsFromTodo={getTagsFromTodo}
              getAreas={getAreas}
              createAttachment={createAttachment}
              downloadAttachment={downloadAttachment}
              editAttachment={editAttachment}
              deleteAttachment={deleteAttachment}
              getTodoAttachments={getTodoAttachments}
            />
          </Fragment>
        )
      },
      [
        clientId,
        classes,
        column.todos,
        columnArray,
        columnIndex,
        createProject,
        decrypt,
        deleteTodo,
        drawerOpen,
        editTodo,
        editTodoStatus,
        getAreas,
        getAreaById,
        getAvailableTags,
        getProjectById,
        getProjectNameForTodo,
        getProjectsForAreas,
        getRepeatPattern,
        getTagsFromTodo,
        createAttachment,
        deleteAttachment,
        editAttachment,
        downloadAttachment,
        getTodoAttachments,
        redirectToProject
      ]
    )
    return (
      <DnDVirtualList
        style={{ height: '100%' }}
        scrollRef={scrollRef}
        droppableId={id}
        rowCount={column.todos.length}
        renderListItem={renderListItem}
        estimatedRowSize={100}
        getRowData={index => {
          return index < column.todos.length ? column.todos[index] : null
        }}
      >
        {(list, isDraggingOver) => (
          <Paper
            className={classes.paper}
            style={{
              background: isDraggingOver
                ? theme.palette.action.focus
                : theme.palette.background.default
            }}
          >
            {wantTitle && (
              <Typography className={classes.title} variant='subtitle1'>
                {column.name}
              </Typography>
            )}
            {list}
          </Paper>
        )}
      </DnDVirtualList>
    )
  }
)

const StyledBadge = withStyles(theme => ({
  badge: {
    right: -7,
    top: 4,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px'
  }
}))(Badge)

const PersonalKanban = ({
  scrollRef,
  getTodos,
  handleTodoDragEnd,
  decrypt,
  getRepeatPattern,
  getProjectsForAreas,
  clientId,
  createProject,
  deleteTodo,
  redirectToProject,
  getProjectById,
  editTodo,
  getAvailableTags,
  getAreas,
  editTodoStatus,
  getProjectNameForTodo,
  getTagsFromTodo,
  cacheDragList,
  getAreaById,
  createAttachment,
  deleteAttachment,
  downloadAttachment,
  editAttachment,
  getTodoAttachments
}) => {
  LOG.debug('Render: PersonalKanban')

  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'), {
    noSsr: true
  })

  const [tabValue, setTabValue] = useState(0)

  const todos = useMemo(() => getTodos(FOCUS.TODAY.id), [getTodos])

  const calculateColumns = useCallback(() => {
    const calculatedColumns = JSON.parse(JSON.stringify(kanbanColumns))

    todos.forEach(todo => {
      if (todo.status && todo.status !== '') {
        calculatedColumns[todo.status].todos = [
          ...calculatedColumns[todo.status].todos,
          todo
        ]
      } else {
        calculatedColumns['todo'].todos = [
          ...calculatedColumns['todo'].todos,
          todo
        ]
      }
    })
    return calculatedColumns
  }, [todos])

  const [columns, setColumns] = useState(calculateColumns())
  const columnArray = useMemo(() => Object.entries(columns), [columns])

  useEffect(() => {
    setColumns(calculateColumns())
  }, [calculateColumns])

  useEffect(() => {
    cacheDragList(FOCUS.TODAY.id, todos)
  }, [cacheDragList, todos])

  // Have to calculate the overall true position from the entire todo list, not just the column
  const calculateTrueDragPosition = useCallback(
    (sourceIndex, sourceColumnId, destinationIndex, destinationColumnId) => {
      LOG.debug(
        'calculateTrueDragPosition',
        sourceIndex,
        sourceColumnId,
        destinationIndex,
        destinationColumnId
      )

      const sourceColumn = columns[sourceColumnId]
      const destColumn = columns[destinationColumnId]

      let answer = 0

      if (destColumn.todos.length === 0) {
        LOG.debug(
          'calculateTrueDragPosition: Empty Destination, do not change position'
        )
        // Destination is empty, only need to change the status, not the position
        answer = todos.findIndex(
          todo => todo.id === sourceColumn.todos[sourceIndex].id
        )
      } else {
        if (destinationIndex === 0) {
          LOG.debug('calculateTrueDragPosition: Top of column')

          // Get the first item in the list and add this in its place
          answer = todos.findIndex(todo => todo.id === destColumn.todos[0].id)
        } else {
          LOG.debug('calculateTrueDragPosition: Mid column')

          if (sourceColumnId === destinationColumnId) {
            LOG.debug('calculateTrueDragPosition: Dragging within same column')

            if (sourceIndex < destinationIndex) {
              answer = todos.findIndex(
                todo => todo.id === destColumn.todos[destinationIndex].id
              )
            } else {
              answer =
                todos.findIndex(
                  todo => todo.id === destColumn.todos[destinationIndex - 1].id
                ) + 1
            }
          } else {
            LOG.debug('calculateTrueDragPosition: Dragging to different column')

            answer =
              todos.findIndex(
                todo => todo.id === destColumn.todos[destinationIndex - 1]?.id
              ) + 1
          }
        }

        if (sourceColumnId !== destinationColumnId) {
          LOG.debug('calculateTrueDragPosition: Dragging to different column')

          const currentPosition = todos.findIndex(
            todo => todo.id === sourceColumn.todos[sourceIndex].id
          )
          LOG.debug(
            'calculateTrueDragPosition: Current: ' +
              currentPosition +
              ' answer: ' +
              answer
          )

          if (currentPosition < answer) {
            // Because we're taking an item away with the drag
            answer--
          }
        }
      }

      LOG.debug('calculateTrueDragPosition: True position is ' + answer)
      return answer
    },
    [todos, columns]
  )

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return
      }

      const { source, destination } = result

      const truePosition = calculateTrueDragPosition(
        source.index,
        source.droppableId,
        destination.index,
        destination.droppableId
      )

      let updatedColumns

      if (source.droppableId !== destination.droppableId) {
        const sourceColumn = columns[source.droppableId]
        const destColumn = columns[destination.droppableId]
        const sourceTodos = [...sourceColumn.todos]
        const destTodos = [...destColumn.todos]
        const [removed] = sourceTodos.splice(source.index, 1)
        destTodos.splice(destination.index, 0, removed)
        updatedColumns = {
          ...columns,
          [source.droppableId]: {
            ...sourceColumn,
            todos: sourceTodos
          },
          [destination.droppableId]: {
            ...destColumn,
            todos: destTodos
          }
        }
      } else {
        const column = columns[source.droppableId]
        const copiedTodos = [...column.todos]
        const [removed] = copiedTodos.splice(source.index, 1)
        copiedTodos.splice(destination.index, 0, removed)
        updatedColumns = {
          ...columns,
          [source.droppableId]: {
            ...column,
            todos: copiedTodos
          }
        }
      }

      setColumns(updatedColumns)

      handleTodoDragEnd(
        result.draggableId,
        FOCUS.TODAY.id,
        truePosition,
        destination.droppableId
      )
    },
    [columns, handleTodoDragEnd, calculateTrueDragPosition]
  )

  const a11yProps = useCallback(index => {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`
    }
  }, [])

  return (
    <div
      className={classes.root}
      style={{ display: isSmall ? 'inherit' : 'flex' }}
    >
      <DragDropContext onDragEnd={result => onDragEnd(result)}>
        <Hidden xsDown>
          <Grid container spacing={isSmall ? 1 : 4} className={classes.grid}>
            {columnArray
              .sort((a, b) => {
                return a[1].position - b[1].position
              })
              .map(([id, column], columnIndex) => {
                return (
                  <Grid key={id} item xs={12 / columnArray.length}>
                    <Column
                      id={id}
                      classes={classes}
                      scrollRef={scrollRef}
                      column={column}
                      columnArray={columnArray}
                      columnIndex={columnIndex}
                      decrypt={decrypt}
                      getRepeatPattern={getRepeatPattern}
                      getProjectsForAreas={getProjectsForAreas}
                      clientId={clientId}
                      createProject={createProject}
                      deleteTodo={deleteTodo}
                      redirectToProject={redirectToProject}
                      getProjectById={getProjectById}
                      editTodo={editTodo}
                      getAvailableTags={getAvailableTags}
                      getAreas={getAreas}
                      editTodoStatus={editTodoStatus}
                      getProjectNameForTodo={getProjectNameForTodo}
                      getTagsFromTodo={getTagsFromTodo}
                      getAreaById={getAreaById}
                      createAttachment={createAttachment}
                      deleteAttachment={deleteAttachment}
                      editAttachment={editAttachment}
                      downloadAttachment={downloadAttachment}
                      getTodoAttachments={getTodoAttachments}
                    />
                  </Grid>
                )
              })}
          </Grid>
        </Hidden>
        <Hidden smUp>
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue)
            }}
            indicatorColor='primary'
            textColor='primary'
            variant='fullWidth'
            aria-label='kanban-tabs'
          >
            {columnArray
              .sort((a, b) => {
                return a[1].position - b[1].position
              })
              .map(([id, column], columnIndex) => {
                return (
                  <Tab
                    key={id}
                    className={classes.tab}
                    label={
                      <StyledBadge
                        className={classes.badge}
                        color='primary'
                        badgeContent={column.todos.length}
                        max={999}
                      >
                        {column.name}
                      </StyledBadge>
                    }
                    {...a11yProps(0)}
                  />
                )
              })}
          </Tabs>
          <Swipeable
            onSwipedLeft={() => {
              LOG.debug('Swiped Left')
              if (tabValue < columnArray.length - 1) {
                setTabValue(tabValue + 1)
              }
            }}
            onSwipedRight={() => {
              LOG.debug('Swiped Right')
              if (tabValue > 0) {
                setTabValue(tabValue - 1)
              }
            }}
          >
            {columnArray
              .sort((a, b) => {
                return a[1].position - b[1].position
              })
              .map(([id, column], columnIndex) => {
                return (
                  <TabPanel value={tabValue} key={id} index={columnIndex}>
                    <Column
                      id={id}
                      classes={classes}
                      scrollRef={scrollRef}
                      column={column}
                      columnArray={columnArray}
                      columnIndex={columnIndex}
                      wantTitle={false}
                      decrypt={decrypt}
                      getRepeatPattern={getRepeatPattern}
                      getProjectsForAreas={getProjectsForAreas}
                      clientId={clientId}
                      createProject={createProject}
                      deleteTodo={deleteTodo}
                      redirectToProject={redirectToProject}
                      getProjectById={getProjectById}
                      editTodo={editTodo}
                      getAvailableTags={getAvailableTags}
                      getAreas={getAreas}
                      editTodoStatus={editTodoStatus}
                      getProjectNameForTodo={getProjectNameForTodo}
                      getTagsFromTodo={getTagsFromTodo}
                      getAreaById={getAreaById}
                      createAttachment={createAttachment}
                      deleteAttachment={deleteAttachment}
                      editAttachment={editAttachment}
                      downloadAttachment={downloadAttachment}
                      getTodoAttachments={getTodoAttachments}
                    />
                  </TabPanel>
                )
              })}
          </Swipeable>
        </Hidden>
      </DragDropContext>
    </div>
  )
}

export default PersonalKanban
